import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Image, Skeleton } from 'antd';
import { getProductByPosRefNum } from '../../../../../../../services/products';
import { setRecentActivity } from '../../../../../../../reducers/client';
import EditActivityModal from '../EditActivityModal';
import { PurchaseTypeTag } from '../../../../../../../componentsV2/Layout/PurchaseTypeTag';
import { BadgeStatus } from '../../../../../../../componentsV2/Routes/Today/Appointments/BadgeStatus';

const CARD_COLOR = {
  purchased: '#33AAFC',
  appointment: '#774fa4',
  activity: '#9C9C9C',
  default: '#F5F5F5'
};

const FILE_RECT_ICONS = {
  pdf: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-pdf.png',
  csv: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-csv.png',
  doc: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-doc.png',
  docx: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-doc.png',
  txt: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-txt.png',
  xls: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-xls.png',
  xlsx: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/new-file-xls.png',
  default: 'https://dashboard-v2-images.s3.us-east-1.amazonaws.com/file-rect-file.svg',
};

const getFileExtension = fileName => {
  return fileName?.split('.').pop()?.toLowerCase() || '';
};

const isImageFile = fileType => {
  return fileType?.startsWith('image/');
};

const BagIcon = props => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M4.50733 4.99707V3.4956C4.50733 1.56012 6.06745 0 8.00293 0C9.93842 0 11.4985 1.56012 11.4985 3.4956V4.99707H13.4927C14.3255 4.99707 14.9941 5.66569 14.9941 6.49854V12.9971C14.9941 14.651 13.6569 16 11.9912 16H4.00293C2.34897 16 1 14.6628 1 12.9971V6.49854C1 5.66569 1.66862 4.99707 2.50147 4.99707H4.4956H4.50733ZM6.0088 4.99707H10.0088V3.4956C10.0088 2.39296 9.1173 1.50146 8.01466 1.50146C6.91202 1.50146 6.02053 2.39296 6.02053 3.4956V4.99707H6.0088ZM2.5132 6.49854V12.9971C2.5132 13.8299 3.18182 14.4985 4.01466 14.4985H12.0147C12.8475 14.4985 13.5161 13.8299 13.5161 12.9971V6.49854H11.522V8.24633C11.522 8.65689 11.1935 8.99707 10.7713 8.99707C10.349 8.99707 10.0205 8.66862 10.0205 8.24633V6.49854H6.02053V8.24633C6.02053 8.65689 5.69208 8.99707 5.26979 8.99707C4.84751 8.99707 4.51906 8.66862 4.51906 8.24633V6.49854H2.5132Z"
      fill="#9C9C9C"
    />
  </svg>
);

const ActivityItem = props => {
  const {
    id,
    title,
    client_id,
    subTitle,
    description,
    associateRefObj,
    created_date,
    notes,
    activityType,
    recentActivity,
    product,
    role,
    transactionCodeTypeDisposition,
    attachments,
    supportsAttachments,
    status,
    onSuccessSave,
  } = props;
  const [openPurchaseDetails, setOpenPurchaseDetails] = useState(false);
  const [showEditModal, toggleEditModal] = useState(false);

  const togglePurchaseDetails = useCallback(() => {
    setOpenPurchaseDetails(!openPurchaseDetails);
  }, [openPurchaseDetails]);

  // Product sku/image aren't included in the sales response; fetch them only once
  // the purchase details drawer is opened (see ENG-3463).
  const { data: productDetails, isFetching: isFetchingProduct } = useQuery(
    ['productByPosRefNum', product?.pos_ref_num],
    () => getProductByPosRefNum({ refNum: product.pos_ref_num }),
    {
      enabled: openPurchaseDetails && !!product?.pos_ref_num,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: 1,
    },
  );

  const productImageUrl = useMemo(() => {
    if (!productDetails?.images?.length) {
      return null;
    }
    const primaryImage = productDetails.images.find(image => image.isPrimary);
    return (primaryImage || productDetails.images[0]).url;
  }, [productDetails]);

  const handleSuccess = useCallback(item => {
    const newRecentActivity = recentActivity.map(i => {
      if (i.id === item.id) {
        return { ...i, notes: item.notes };
      }

      return i;
    });
    if (onSuccessSave) {
      onSuccessSave();
    }

    setRecentActivity(newRecentActivity);
    toggleEditModal(false);
  }, []);

  return (
    <>
      <div className="cb-activity-item-container m-right-4 m-left-4">
        <div
          className="cb-activity-strip"
          style={{ background: CARD_COLOR[activityType] || CARD_COLOR.default }}
        />
        <div className="flex-row-spacebetween-start">
          <div className="cb-activity-item-details">
            <p className="fs-16 fw-500 m-btm-8">
              {title}{' '}
              {((role === 'ADMIN' && activityType === 'activity') || role === 'MANAGER') ? (
                supportsAttachments ? (
                  <span className="cb-activity-item--edit-button" onClick={() => toggleEditModal(true)}>
                    Edit
                  </span>
                ) : (
                  <span style={{ color: '#999', fontSize: '12px', fontWeight: 400 }}>View only</span>
                )
              ) : null}
            </p>
            {notes && <p className="fs-14 fw-400">{notes}</p>}
            <div>
              <p className="fs-15 fw-500 m-btm-8">
                {activityType === 'purchased' ? (
                  <div className="flex-row-center gap-8">
                    <BagIcon />
                    {`$${subTitle}`}
                    <PurchaseTypeTag type={transactionCodeTypeDisposition} />
                  </div>
                ) : (
                  subTitle
                )}
              </p>
              <p className="fs-13 fw-400">{associateRefObj[description] || description}</p>
            </div>
          </div>
          <div className="flex-col-right" style={{ alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
            <div className="cb-activity-item-date">
              {activityType === 'appointment' && (
                <div className="m-right-8">
                  <BadgeStatus status={status} />
                </div>
              )}
              <span>{moment(created_date, 'YYYYMMDDHHmmss').format('MM/DD/yyyy')}</span>
              {activityType === 'purchased' && (
                <FontAwesomeIcon
                  icon={`fa-light ${openPurchaseDetails ? 'fa-chevron-down' : 'fa-chevron-right'}`}
                  className="pointer"
                  onClick={togglePurchaseDetails}
                />
              )}
            </div>
            {attachments?.length > 0 && (
              <div className="flex-row-center" style={{ gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {attachments.slice(0, 4).map(attachment => {
                  const ext = getFileExtension(attachment.fileName);
                  const icon = FILE_RECT_ICONS[ext] || FILE_RECT_ICONS.default;
                  return isImageFile(attachment.fileType) ? (
                    <Image
                      key={attachment.id}
                      src={attachment.url}
                      width={50}
                      height={50}
                      style={{ borderRadius: '4px', objectFit: 'cover' }}
                      fallback="https://dashboard-v2-images.s3.amazonaws.com/not-found.png"
                    />
                  ) : (
                    <img
                      key={attachment.id}
                      src={icon}
                      alt={attachment.fileName || 'file'}
                      style={{ width: 50, height: 50, objectFit: 'contain' }}
                    />
                  );
                })}
                {attachments.length > 4 && (
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: '4px',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#666',
                    }}
                  >
                    +{attachments.length - 4}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {activityType === 'purchased' && (
          <div className={`cb-activity-purchase-details ${openPurchaseDetails ? 'show-effect' : 'hide-effect hide'}`}>
            {isFetchingProduct ? (
              <Skeleton.Image style={{ width: 150, height: 150, minWidth: '150px', borderRadius: '6px' }} />
            ) : (
              <Image
                src={productImageUrl || 'https://dashboard-v2-images.s3.amazonaws.com/not-found.png'}
                width={150}
                height={150}
                style={{ minWidth: '150px', borderRadius: '6px' }}
                fallback="https://dashboard-v2-images.s3.amazonaws.com/not-found.png"
              />
            )}
            <div className="flex-col-left">
              <p className="line-clamp-1">{product?.name}</p>
              <div className="cb-activity-purchase-details-grade">
                <div className="cb-activity-purchase-details-label">POS ID</div>
                <div className="cb-activity-purchase-details-value">{product?.pos_ref_num}</div>
                <div className="cb-activity-purchase-details-label">SKU</div>
                <div className="cb-activity-purchase-details-value">
                  {isFetchingProduct ? (
                    <Skeleton active paragraph={false} title={{ width: 80 }} />
                  ) : (
                    productDetails?.sku
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showEditModal ? (
        <EditActivityModal
          id={id}
          notes={notes}
          clientId={client_id}
          attachments={attachments}
          supportsAttachments={supportsAttachments}
          showEditModal={showEditModal}
          toggleEditModal={toggleEditModal}
          onSuccess={handleSuccess}
        />
      ) : null}
    </>
  );
};

export default ActivityItem;
